/** First-run walkthrough for the Recruitment Network (/app/network).
 *
 *  A centred step card over a dimmed canvas — deliberately NOT anchored spotlights: the graph pans,
 *  zooms and reflows on resize, so a pointer pinned to a node would drift off its target. Opens
 *  itself once per user (remembered in this browser), can be skipped at any step, and comes back
 *  from "Getting started guide" in the profile menu, which links here as ?tour=1. */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, UploadCloud, Workflow, MousePointerClick, SlidersHorizontal, Orbit, Rocket, X, ChevronLeft, ChevronRight,
} from "lucide-react";

const KEY = "th.networkTour.done";

/** Has this user finished or skipped the walkthrough in this browser? Storage failures (private
 *  window, blocked site data) read as "seen" so a broken store never nags on every visit. */
export function networkTourSeen(userId: string): boolean {
  try {
    return localStorage.getItem(`${KEY}.${userId}`) === "1";
  } catch {
    return true;
  }
}

function markSeen(userId: string): void {
  try {
    localStorage.setItem(`${KEY}.${userId}`, "1");
  } catch {
    /* nothing to do — the tour just may open again next time */
  }
}

interface Step {
  icon: ReactNode;
  title: string;
  body: ReactNode;
}

const STEPS: Step[] = [
  {
    icon: <Workflow size={22} />,
    title: "Welcome to your Recruitment Network",
    body: (
      <>
        This view draws one hiring requirement as a live map: the <b>requirement</b> in the middle, its
        <b> pipeline stages</b> around it, and every <b>candidate</b> connected to the stage they’ve reached.
        Here’s a one-minute tour — you can skip it and reopen it any time from your profile menu.
      </>
    ),
  },
  {
    icon: <Briefcase size={22} />,
    title: "1 · Start with a requirement",
    body: (
      <>
        Everything hangs off a requirement (a job you’re hiring for). Use <b>New requirement</b> to describe the
        role — we turn it into the skills and interview plan candidates are measured against. Switch between
        requirements with the <b>Focused requirement</b> picker at the top left.
      </>
    ),
  },
  {
    icon: <UploadCloud size={22} />,
    title: "2 · Bring in candidates",
    body: (
      <>
        Upload CVs, add someone by name and email, or forward applications to the requirement’s own email
        address. Each resume is scored against the requirement and appears under <b>Resume Scanning</b>.
      </>
    ),
  },
  {
    icon: <MousePointerClick size={22} />,
    title: "3 · Follow the pipeline",
    body: (
      <>
        Click a <b>stage</b> — Applications Received, Resume Scanning, Interview Invitation, Interview Completed — to see who’s
        there, ranked best-first. Click a <b>candidate</b> to open their details, scores and next actions. The
        engine ranks; you decide who moves on.
      </>
    ),
  },
  {
    icon: <SlidersHorizontal size={22} />,
    title: "4 · Find your way around",
    body: (
      <>
        Drag the background to pan, scroll or use the zoom buttons to zoom, and drag any card to move it.
        <b> Filters</b> narrow the graph to progressing, interviewed, pending or rejected candidates;
        <b> Reset</b> puts the layout back.
      </>
    ),
  },
  {
    icon: <Orbit size={22} />,
    title: "5 · See every requirement at once",
    body: (
      <>
        Switch to <b>Constellation</b> for a bird’s-eye view of all your requirements — active, on hold and
        closed. Click any one to jump back into its focused network.
      </>
    ),
  },
];

export function NetworkTour({
  userId,
  hasRequirements,
  onClose,
}: {
  userId: string;
  hasRequirements: boolean;
  onClose: () => void;
}): ReactNode {
  const [i, setI] = useState(0);
  const last = i === STEPS.length; // the closing "you're ready" card
  const finish = (): void => {
    markSeen(userId);
    onClose();
  };

  // Seen = shown. Navigating away (top-bar link, reload) used to leave the flag unset, so the tour
  // reopened on every visit until someone pressed Skip. Mark it on first show; ?tour=1 reopens it.
  useEffect(() => {
    markSeen(userId);
  }, [userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") finish();
      else if (e.key === "ArrowRight") setI((n) => Math.min(n + 1, STEPS.length));
      else if (e.key === "ArrowLeft") setI((n) => Math.max(n - 1, 0));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // finish only closes over stable props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step: Step = last
    ? {
        icon: <Rocket size={22} />,
        title: "You’re ready",
        body: hasRequirements ? (
          <>Pick a requirement at the top left and click a stage to see its candidates.</>
        ) : (
          <>Create your first requirement and its network will appear here as candidates arrive.</>
        ),
      }
    : STEPS[i]!;

  return (
    <div className="rn-tour" data-no-pan role="dialog" aria-modal="true" aria-labelledby="rn-tour-title">
      <div className="rn-tour-card">
        <button type="button" className="rn-tour-x" onClick={finish} aria-label="Close walkthrough" data-tip="Close" data-tip-pos="left">
          <X size={16} />
        </button>
        <div className="rn-tour-icon">{step.icon}</div>
        <h2 id="rn-tour-title" className="rn-tour-title">{step.title}</h2>
        <span className="rn-tour-count">Step {i + 1} of {STEPS.length + 1}</span>
        <p className="rn-tour-body">{step.body}</p>

        <div className="rn-tour-dots" aria-label={`Step ${i + 1} of ${STEPS.length + 1}`}>
          {[...STEPS, null].map((_, n) => (
            <button
              key={n}
              type="button"
              className={n === i ? "is-on" : ""}
              onClick={() => setI(n)}
              aria-label={`Go to step ${n + 1}`}
            />
          ))}
        </div>

        <div className="rn-tour-actions">
          {last ? (
            <>
              <button type="button" className="rn-tool wide" onClick={() => setI(i - 1)}>
                <ChevronLeft size={15} /> Back
              </button>
              {hasRequirements ? (
                <button type="button" className="rn-tour-primary" onClick={finish}>Explore my network</button>
              ) : (
                <Link className="rn-tour-primary" to="/app/new" onClick={finish}>Create your first requirement</Link>
              )}
            </>
          ) : (
            <>
              <button type="button" className="rn-tour-skip" onClick={finish}>Skip tour</button>
              <span style={{ flex: 1 }} />
              {i > 0 && (
                <button type="button" className="rn-tool wide" onClick={() => setI(i - 1)}>
                  <ChevronLeft size={15} /> Back
                </button>
              )}
              <button type="button" className="rn-tour-primary" onClick={() => setI(i + 1)} autoFocus>
                Next <ChevronRight size={15} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
